import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { getFaturamentoStatusClasse } from '../utils/faturamentoStatusUtils'
import { useSearchParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { DollarSign, Search, X, Download, XCircle, Eye, Filter, ChevronLeft, ChevronRight } from 'lucide-react'
import { useToast } from '../../../contexts/NotificationContext'
import { useGlobalLoading } from '../../../contexts/LoadingContext'
import { zRequiredDate, zOptionalDate } from '../../../utils/validacao'
import { faturamentoService, FaturamentoDto, AnalistaSMNDto, FaturamentoDetalhesDto, EtapaFaturamentoDto, FaturamentoStatusDto } from '../services/faturamento'
import { Modal } from '../../../componentesCompartilhados/ui/Modal'
import { ConfirmDialog } from '../../../componentesCompartilhados/ui/ConfirmDialog'
import { InputDate } from '../../../componentesCompartilhados/form'
import { format } from 'date-fns'
import { formatDateToBrazilian } from '../../../utils/formatUtils'
import { FaturamentoDetalhesConteudo } from '../componentes/FaturamentoDetalhesConteudo'
import { useForm as useFormDocumento } from 'react-hook-form'
import { projetoService } from '../services/projeto'
import { SeletorPeriodo, resolverDatasPeriodo, type TipoPeriodoPreset } from '../../../componentesCompartilhados/periodo'

interface FaturamentoFilters {
  numeroTicket?: string
  nomeProjeto?: string
  numeroNotaFiscal?: string
  dataInicio: string
  dataFim?: string
}

const faturamentoSchema = z.object({
  numeroTicket: z.string().optional(),
  nomeProjeto: z.string().optional(),
  numeroNotaFiscal: z.string().optional(),
  dataInicio: zRequiredDate,
  dataFim: zOptionalDate,
}).superRefine((data, ctx) => {
  // Validar que data fim seja maior que data início
  if (data.dataFim && data.dataInicio && typeof data.dataFim === 'string' && typeof data.dataInicio === 'string') {
    const dataInicio = new Date(data.dataInicio)
    const dataFim = new Date(data.dataFim)
    
    if (!isNaN(dataInicio.getTime()) && !isNaN(dataFim.getTime()) && dataFim < dataInicio) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['dataFim'],
        message: 'Data fim deve ser maior ou igual à data início',
      })
    }
  }
})

export default function FaturamentoAdministrador() {
  const toast = useToast()
  const { startLoading, stopLoading } = useGlobalLoading()
  const loadingRef = useRef({ startLoading, stopLoading })
  const toastRef = useRef(toast)
  const [searchParams, setSearchParams] = useSearchParams()
  const [faturamentos, setFaturamentos] = useState<FaturamentoDto[]>([])
  const [analistasSMN, setAnalistasSMN] = useState<AnalistaSMNDto[]>([])
  const [statusFaturamento, setStatusFaturamento] = useState<FaturamentoStatusDto[]>([])
  const [statusSelecionados, setStatusSelecionados] = useState<number[]>([])
  const [dropdownStatusAberto, setDropdownStatusAberto] = useState(false)
  const dropdownStatusRef = useRef<HTMLDivElement>(null)
  const [analistasSelecionados, setAnalistasSelecionados] = useState<number[]>([])
  const [dropdownAnalistaAberto, setDropdownAnalistaAberto] = useState(false)
  const dropdownAnalistaRef = useRef<HTMLDivElement>(null)
  const [isSearching, setIsSearching] = useState(false)
  const [showModalDetalhes, setShowModalDetalhes] = useState(false)
  const [showModalReprovar, setShowModalReprovar] = useState(false)
  const [faturamentoSelecionado, setFaturamentoSelecionado] = useState<FaturamentoDto | null>(null)
  const [detalhesFaturamento, setDetalhesFaturamento] = useState<FaturamentoDetalhesDto | null>(null)
  const [motivoReprovacao, setMotivoReprovacao] = useState('')
  const [showModalReprovarAdministrativo, setShowModalReprovarAdministrativo] = useState(false)
  const [motivoReprovacaoAdministrativa, setMotivoReprovacaoAdministrativa] = useState('')
  const [dataReprovacaoAdministrativa, setDataReprovacaoAdministrativa] = useState('')
  const [datasReprovacao, setDatasReprovacao] = useState<Record<number, string>>({})
  const [datasEnvio, setDatasEnvio] = useState<Record<number, string>>({})
  const [datasAprovacao, setDatasAprovacao] = useState<Record<number, string>>({})
  const [datasFaturamento, setDatasFaturamento] = useState<Record<number, string>>({})
  const [nfs, setNfs] = useState<Record<number, string>>({})
  const [datasEnvioOrig, setDatasEnvioOrig] = useState<Record<number, string>>({})
  const [datasAprovacaoOrig, setDatasAprovacaoOrig] = useState<Record<number, string>>({})
  const [datasFaturamentoOrig, setDatasFaturamentoOrig] = useState<Record<number, string>>({})
  const [nfsOrig, setNfsOrig] = useState<Record<number, string>>({})
  const [showConfirmEdicao, setShowConfirmEdicao] = useState(false)
  const [pendenciaEdicao, setPendenciaEdicao] = useState<{
    idFaturamento: number
    campo: 'dataEnvioMomentum' | 'dataAprovacao' | 'dataFaturamento' | 'nf'
    valor: string
  } | null>(null)
  const [_showFormDocumento, setShowFormDocumento] = useState(false)
  const [documentoEditando, setDocumentoEditando] = useState<any>(null)
  const [_tipoDocumentoSelecionado, setTipoDocumentoSelecionado] = useState<4 | 5 | null>(null)
  const [etapasFaturamento, setEtapasFaturamento] = useState<EtapaFaturamentoDto[]>([])
  const [faturamentosProjeto, setFaturamentosProjeto] = useState<FaturamentoDto[]>([])
  const [pagina, setPagina] = useState(1)
  const [buscaRealizada, setBuscaRealizada] = useState(false)
  const [tipoPeriodo, setTipoPeriodo] = useState<TipoPeriodoPreset>('ultimos30')
  const [dataReferenciaPeriodo, setDataReferenciaPeriodo] = useState(() => format(new Date(), 'yyyy-MM-dd'))
  const [intervaloInicio, setIntervaloInicio] = useState('')
  const [intervaloFim, setIntervaloFim] = useState('')
  const [idsSelecionados, setIdsSelecionados] = useState<Set<number>>(() => new Set())
  const [pulsoBuscaAposAplicarPeriodo, setPulsoBuscaAposAplicarPeriodo] = useState(0)

  const PAGE_SIZE = 50
  const faturamentosPagina = useMemo(() => {
    const start = (pagina - 1) * PAGE_SIZE
    return faturamentos.slice(start, start + PAGE_SIZE)
  }, [faturamentos, pagina])
  const totalPaginas = Math.max(1, Math.ceil(faturamentos.length / PAGE_SIZE))

  const somaValorSelecionados = useMemo(() => {
    let total = 0
    for (let i = 0; i < faturamentos.length; i++)
    {
      const f = faturamentos[i]
      if (idsSelecionados.has(f.idFaturamento))
        total += f.valorTotal
    }
    return total
  }, [faturamentos, idsSelecionados])

  const idsPaginaAtual = useMemo(
    () => faturamentosPagina.map((f) => f.idFaturamento),
    [faturamentosPagina],
  )
  const todosDaPaginaSelecionados =
    idsPaginaAtual.length > 0 && idsPaginaAtual.every((id) => idsSelecionados.has(id))

  const alternarSelecaoPorId = (id: number) => {
    setIdsSelecionados((prev) => {
      const n = new Set(prev)
      if (n.has(id))
        n.delete(id)
      else
        n.add(id)
      return n
    })
  }

  const alternarSelecionarPaginaAtual = () => {
    setIdsSelecionados((prev) => {
      const n = new Set(prev)
      if (todosDaPaginaSelecionados)
      {
        for (let i = 0; i < idsPaginaAtual.length; i++)
          n.delete(idsPaginaAtual[i])
      }
      else
      {
        for (let i = 0; i < idsPaginaAtual.length; i++)
          n.add(idsPaginaAtual[i])
      }
      return n
    })
  }

  const { register: _registerDocumento, handleSubmit: _handleSubmitDocumento, formState: { errors: _errorsDocumento }, reset: resetDocumento, setValue: _setValueDocumento, watch: _watchDocumento } = useFormDocumento<{
    nomeArquivo: string
    tipoDocumentoCodigo: string
    arquivo: File | null
  }>({
    defaultValues: {
      nomeArquivo: '',
      tipoDocumentoCodigo: '',
      arquivo: null
    }
  })
  
  // @ts-expect-error - Variável mantida para uso futuro
  const _selectedFileDocumento = _watchDocumento('arquivo')

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    getValues,
  } = useForm<FaturamentoFilters>({
    resolver: zodResolver(faturamentoSchema),
    mode: 'onSubmit',
    defaultValues: {
      numeroTicket: '',
      nomeProjeto: '',
      numeroNotaFiscal: '',
      dataInicio: '',
      dataFim: '',
    },
  })

  useEffect(() => {
    const { dataInicio: di, dataFim: df } = resolverDatasPeriodo(tipoPeriodo, dataReferenciaPeriodo, intervaloInicio, intervaloFim)
    setValue('dataInicio', di)
    setValue('dataFim', df)
  }, [tipoPeriodo, dataReferenciaPeriodo, intervaloInicio, intervaloFim, setValue])

  useEffect(() => {
    loadingRef.current = { startLoading, stopLoading }
  }, [startLoading, stopLoading])

  useEffect(() => {
    toastRef.current = toast
  }, [toast])

  const onSubmitForm = useCallback(async (data: FaturamentoFilters) => {
    if (tipoPeriodo === 'intervalo')
    {
      const ai = intervaloInicio.trim()
      if (!/^\d{4}-\d{2}-\d{2}$/.test(ai))
      {
        toastRef.current.error('Período', 'Informe a data inicial no período personalizado')
        return
      }
      const afRaw = intervaloFim.trim()
      const af = afRaw && /^\d{4}-\d{2}-\d{2}$/.test(afRaw) ? afRaw : format(new Date(), 'yyyy-MM-dd')
      if (af < ai)
      {
        toastRef.current.error('Período', 'A data final deve ser igual ou posterior à inicial')
        return
      }
    }
    setBuscaRealizada(true)
    setIsSearching(true)
    loadingRef.current.startLoading('Buscando faturamentos...')
    try {
      const dataFimFinal = data.dataFim || format(new Date(), 'yyyy-MM-dd')
      
      const filtros = {
        numeroTicket: data.numeroTicket?.trim() || undefined,
        nomeProjeto: data.nomeProjeto?.trim() || undefined,
        numeroNotaFiscal: data.numeroNotaFiscal?.trim() || undefined,
        analistaSMNIds: analistasSelecionados.length > 0 && analistasSelecionados.length < analistasSMN.length
          ? analistasSelecionados
          : undefined,
        dataInicio: data.dataInicio,
        dataFim: dataFimFinal,
        statusCodigos: statusSelecionados.length > 0 && statusSelecionados.length < statusFaturamento.length
          ? statusSelecionados
          : undefined,
      }

      const resultado = await faturamentoService.buscarFaturamentos(filtros)
      setFaturamentos(resultado)
      setPagina(1)
      setIdsSelecionados(new Set())

      const datasEnvioMap: Record<number, string> = {}
      const datasAprovacaoMap: Record<number, string> = {}
      const datasFaturamentoMap: Record<number, string> = {}
      const nfsMap: Record<number, string> = {}

      resultado.forEach(f => {
        datasEnvioMap[f.idFaturamento] = f.dataEnvioMomentum ? f.dataEnvioMomentum.split('T')[0] : ''
        datasAprovacaoMap[f.idFaturamento] = f.dataAprovacao ? f.dataAprovacao.split('T')[0] : ''
        datasFaturamentoMap[f.idFaturamento] = f.dataFaturamento ? f.dataFaturamento.split('T')[0] : ''
        nfsMap[f.idFaturamento] = f.nf ? String(f.nf) : ''
      })

      setDatasEnvio(datasEnvioMap)
      setDatasAprovacao(datasAprovacaoMap)
      setDatasFaturamento(datasFaturamentoMap)
      setNfs(nfsMap)
      setDatasEnvioOrig(datasEnvioMap)
      setDatasAprovacaoOrig(datasAprovacaoMap)
      setDatasFaturamentoOrig(datasFaturamentoMap)
      setNfsOrig(nfsMap)
    } catch (error: any) {
      toastRef.current.error('Erro', error.message || 'Erro ao buscar faturamentos')
    } finally {
      setIsSearching(false)
      loadingRef.current.stopLoading()
    }
  }, [statusSelecionados, statusFaturamento, analistasSelecionados, analistasSMN, tipoPeriodo, intervaloInicio, intervaloFim])

  useEffect(() => {
    carregarAnalistasSMN()
    const carregarEtapas = async () => {
      try {
        loadingRef.current.startLoading('Carregando etapas...')
        const etapas = await faturamentoService.obterEtapasFaturamento()
        setEtapasFaturamento(etapas)
      } catch (error: any) {
      } finally {
        loadingRef.current.stopLoading()
      }
    }
    carregarEtapas()

    const carregarStatus = async () => {
      try {
        loadingRef.current.startLoading('Carregando status...')
        const status = await faturamentoService.obterStatusFaturamento()
        setStatusFaturamento(status)
      } catch (error: any) {
        toastRef.current.error('Erro', error.message || 'Erro ao carregar status de faturamento')
      } finally {
        loadingRef.current.stopLoading()
      }
    }
    carregarStatus()
  }, [])

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickFora = (e: MouseEvent) => {
      if (dropdownStatusRef.current && !dropdownStatusRef.current.contains(e.target as Node))
        setDropdownStatusAberto(false)
      if (dropdownAnalistaRef.current && !dropdownAnalistaRef.current.contains(e.target as Node))
        setDropdownAnalistaAberto(false)
    }
    document.addEventListener('mousedown', handleClickFora)
    return () => document.removeEventListener('mousedown', handleClickFora)
  }, [])

  // Ref para sempre chamar a versão mais recente de onSubmitForm sem re-disparar o effect
  const onSubmitFormRef = useRef(onSubmitForm)
  useEffect(() => {
    onSubmitFormRef.current = onSubmitForm
  }, [onSubmitForm])

  const getValuesRef = useRef(getValues)
  getValuesRef.current = getValues

  useEffect(() => {
    if (pulsoBuscaAposAplicarPeriodo === 0)
      return
    void onSubmitFormRef.current(getValuesRef.current())
  }, [pulsoBuscaAposAplicarPeriodo])

  // Ler parâmetro da URL e fazer busca automática
  const pendenciaBuscaRef = useRef(false)
  // Controla se os valores da pendência já foram aplicados para não sobrescrever edições do usuário
  const pendenciaAplicadaRef = useRef(false)
  useEffect(() => {
    const numeroTicket = searchParams.get('numeroTicket')
    if (numeroTicket) {
      pendenciaBuscaRef.current = true

      // Só aplica setValue uma única vez; re-execuções do effect não sobrescrevem edições do usuário
      if (pendenciaAplicadaRef.current)
        return

      pendenciaAplicadaRef.current = true

      const dataFim = format(new Date(), 'yyyy-MM-dd')
      const dataInicioDt = new Date()
      dataInicioDt.setMonth(dataInicioDt.getMonth() - 3)
      const dataInicioStr = format(dataInicioDt, 'yyyy-MM-dd')

      setValue('numeroTicket', numeroTicket)
      setTipoPeriodo('intervalo')
      setIntervaloInicio(dataInicioStr)
      setIntervaloFim(dataFim)
      setStatusSelecionados([])
      
      const ticketSnapshot = numeroTicket
      const dataInicioSnapshot = dataInicioStr
      const dataFimSnapshot = dataFim

      const timer = setTimeout(async () => {
        const formData: FaturamentoFilters = {
          numeroTicket: ticketSnapshot,
          nomeProjeto: '',
          numeroNotaFiscal: '',
          dataInicio: dataInicioSnapshot,
          dataFim: dataFimSnapshot,
        }
        await onSubmitFormRef.current(formData)
        
        const newSearchParams = new URLSearchParams(searchParams)
        newSearchParams.delete('numeroTicket')
        setSearchParams(newSearchParams, { replace: true })
        pendenciaAplicadaRef.current = false
      }, 300)
      
      return () => clearTimeout(timer)
    }
  }, [searchParams, setValue, setSearchParams])

  // Busca inicial: ao carregar a página, trazer faturamentos sem nota fiscal (últimos 12 meses)
  const inicialLoadFeito = useRef(false)
  useEffect(() => {
    if (pendenciaBuscaRef.current || searchParams.get('numeroTicket'))
      return
    if (inicialLoadFeito.current)
      return
    inicialLoadFeito.current = true
    setBuscaRealizada(true)
    const hojeIso = format(new Date(), 'yyyy-MM-dd')
    const { dataInicio: dataInicioStr, dataFim: dataFim } = resolverDatasPeriodo('ultimos30', hojeIso, '', '', new Date())
    setValue('dataInicio', dataInicioStr)
    setValue('dataFim', dataFim)
    setIsSearching(true)
    loadingRef.current.startLoading('Carregando faturamentos sem nota fiscal...')
    faturamentoService.buscarFaturamentos({
      dataInicio: dataInicioStr,
      dataFim,
      semNotaFiscal: true,
    })
      .then((resultado) => {
        setFaturamentos(resultado)
        setPagina(1)
        const datasEnvioMap: Record<number, string> = {}
        const datasAprovacaoMap: Record<number, string> = {}
        const nfsMap: Record<number, string> = {}
        setIdsSelecionados(new Set())
        resultado.forEach((f) => {
          datasEnvioMap[f.idFaturamento] = f.dataEnvioMomentum ? f.dataEnvioMomentum.split('T')[0] : ''
          datasAprovacaoMap[f.idFaturamento] = f.dataAprovacao ? f.dataAprovacao.split('T')[0] : ''
          nfsMap[f.idFaturamento] = f.nf ? String(f.nf) : ''
        })
        setDatasEnvio(datasEnvioMap)
        setDatasAprovacao(datasAprovacaoMap)
        setNfs(nfsMap)
        setDatasEnvioOrig(datasEnvioMap)
        setDatasAprovacaoOrig(datasAprovacaoMap)
        setNfsOrig(nfsMap)
      })
      .catch((error: any) => {
        toastRef.current.error('Erro', error?.message || 'Erro ao carregar faturamentos')
      })
      .finally(() => {
        setIsSearching(false)
        loadingRef.current.stopLoading()
      })
  }, [searchParams, setValue])

  const carregarAnalistasSMN = async () => {
    try {
      loadingRef.current.startLoading('Carregando analistas...')
      const analistas = await faturamentoService.obterAnalistasSMN()
      setAnalistasSMN(analistas)
    } catch (error: any) {
      toastRef.current.error('Erro', error.message || 'Erro ao carregar analistas SMN')
    } finally {
      loadingRef.current.stopLoading()
    }
  }

  const limparFiltros = () => {
    reset({
      numeroTicket: '',
      nomeProjeto: '',
      numeroNotaFiscal: '',
      dataInicio: '',
      dataFim: '',
    })
    setStatusSelecionados([])
    setAnalistasSelecionados([])
    setTipoPeriodo('ultimos30')
    setDataReferenciaPeriodo(format(new Date(), 'yyyy-MM-dd'))
    setIntervaloInicio('')
    setIntervaloFim('')
    setFaturamentos([])
    setPagina(1)
    setIdsSelecionados(new Set())
  }

  const obterClasseStatus = getFaturamentoStatusClasse

  const handleVisualizar = async (faturamento: FaturamentoDto) => {
    try {
      startLoading('Carregando faturamento...')
      const detalhes = await faturamentoService.obterDetalhes(faturamento.idFaturamento)
      setDetalhesFaturamento(detalhes)
      setFaturamentoSelecionado(faturamento)
      
      // Buscar todos os faturamentos do projeto para calcular o saldo
      const faturamentos = await faturamentoService.obterFaturamentosPorProjeto(faturamento.idProjeto)
      setFaturamentosProjeto(faturamentos)
      
      setShowModalDetalhes(true)
      setShowFormDocumento(false)
      setTipoDocumentoSelecionado(null)
      setDocumentoEditando(null)
      resetDocumento()
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao obter detalhes do faturamento')
    } finally {
      stopLoading()
    }
  }
  
  // @ts-expect-error - Função mantida para uso futuro
  const _handleAbrirFormDocumento = (tipo: 4 | 5) => {
    setTipoDocumentoSelecionado(tipo)
    setShowFormDocumento(true)
    setDocumentoEditando(null)
    resetDocumento({
      nomeArquivo: '',
      tipoDocumentoCodigo: tipo.toString(),
      arquivo: null
    })
  }
  
  // @ts-expect-error - Função mantida para uso futuro
  const _handleEditarDocumento = (doc: any) => {
    setDocumentoEditando(doc)
    setTipoDocumentoSelecionado(doc.tipoDocumentoCodigo as 4 | 5)
    setShowFormDocumento(true)
    resetDocumento({
      nomeArquivo: doc.nomeArquivo || doc.tipoDocumentoNome || '',
      tipoDocumentoCodigo: doc.tipoDocumentoCodigo.toString(),
      arquivo: null
    })
  }
  
  const handleCancelarDocumento = () => {
    setShowFormDocumento(false)
    setTipoDocumentoSelecionado(null)
    setDocumentoEditando(null)
    resetDocumento()
  }
  
  // @ts-expect-error - Função mantida para uso futuro
  const _handleSalvarDocumento = async (data: { nomeArquivo: string; tipoDocumentoCodigo: string; arquivo: File | null }) => {
    if (!detalhesFaturamento) return
    
    if (!documentoEditando && !data.arquivo) {
      toast.error('Erro', 'Arquivo é obrigatório')
      return
    }
    
    try {
      startLoading('Enviando documento...')
      
      if (documentoEditando) {
        await projetoService.atualizarDocumento(
          documentoEditando.idDocumento,
          detalhesFaturamento.idProjeto,
          Number(data.tipoDocumentoCodigo),
          data.arquivo || undefined,
          data.nomeArquivo
        )
        toast.success('Sucesso', 'Documento atualizado com sucesso!')
      } else {
        if (!data.arquivo) {
          toast.error('Erro', 'Arquivo é obrigatório')
          return
        }
        await faturamentoService.uploadDocumento(
          detalhesFaturamento.idProjeto,
          Number(data.tipoDocumentoCodigo),
          data.arquivo,
          data.nomeArquivo
        )
        toast.success('Sucesso', 'Documento enviado com sucesso!')
      }
      
      // Recarregar detalhes para atualizar documentos
      const detalhesAtualizados = await faturamentoService.obterDetalhes(detalhesFaturamento.idFaturamento)
      setDetalhesFaturamento(detalhesAtualizados)
      
      handleCancelarDocumento()
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao salvar documento')
    } finally {
      stopLoading()
    }
  }
  
  // @ts-expect-error - Função mantida para uso futuro
  const _handleDownloadDocumento = async (idDocumento: number, nomeArquivo: string) => {
    try {
      startLoading('Baixando documento...')
      const blob = await faturamentoService.downloadDocumento(idDocumento)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = nomeArquivo
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      toast.success('Sucesso', 'Documento baixado com sucesso!')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao baixar documento')
    } finally {
      stopLoading()
    }
  }

  const handleDownloadDocumentos = async (faturamentoId: number) => {
    startLoading('Gerando arquivo ZIP com documentos...')
    try {
      const blob = await faturamentoService.downloadDocumentos(faturamentoId)
      
      // Obter nome do projeto para o nome do arquivo
      const faturamento = faturamentos.find(f => f.idFaturamento === faturamentoId)
      const nomeProjeto = faturamento?.projetoNome || 'Projeto'
      const nomeArquivo = nomeProjeto
        .replace(/[/\\:*?"<>|]/g, '-')
        .trim()
      
      // Criar link para download
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${nomeArquivo}.zip`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
      
      toast.success('Download iniciado', 'Os documentos estão sendo baixados')
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao fazer download dos documentos')
    } finally {
      stopLoading()
    }
  }

  const handleReprovar = (faturamento: FaturamentoDto) => {
    setFaturamentoSelecionado(faturamento)
    setMotivoReprovacao('')
    setShowModalReprovar(true)
  }

  const confirmarReprovar = async () => {
    if (!faturamentoSelecionado || !motivoReprovacao.trim()) {
      toast.error('Erro', 'Informe o motivo da reprovação')
      return
    }

    try {
      startLoading('Reprovando faturamento...')
      await faturamentoService.reprovar(faturamentoSelecionado.idFaturamento, motivoReprovacao)
      toast.success('Sucesso', 'Faturamento reprovado com sucesso!')
      setShowModalReprovar(false)
      setFaturamentoSelecionado(null)
      setMotivoReprovacao('')
      
      // Remover da lista ou atualizar
      setIdsSelecionados((prev) => {
        const n = new Set(prev)
        n.delete(faturamentoSelecionado.idFaturamento)
        return n
      })
      setFaturamentos(faturamentos.filter(f => f.idFaturamento !== faturamentoSelecionado.idFaturamento))
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao reprovar faturamento')
    } finally {
      stopLoading()
    }
  }

  const handleDataReprovacaoBlur = (faturamento: FaturamentoDto) => {
    const dataReprovacao = datasReprovacao[faturamento.idFaturamento]?.trim()
    if (!dataReprovacao) {
      return
    }

    setFaturamentoSelecionado(faturamento)
    setDataReprovacaoAdministrativa(dataReprovacao)
    setMotivoReprovacaoAdministrativa('')
    setShowModalReprovarAdministrativo(true)
  }

  const confirmarReprovarAdministrativo = async () => {
    if (!faturamentoSelecionado || !dataReprovacaoAdministrativa.trim() || !motivoReprovacaoAdministrativa.trim()) {
      toast.error('Erro', 'Informe a data e o motivo da reprovação')
      return
    }

    try {
      startLoading('Reprovando faturamento...')
      await faturamentoService.reprovarAdministrativo(
        faturamentoSelecionado.idFaturamento,
        dataReprovacaoAdministrativa,
        motivoReprovacaoAdministrativa
      )

      toast.success('Sucesso', 'Faturamento reprovado administrativamente!')
      setShowModalReprovarAdministrativo(false)
      setMotivoReprovacaoAdministrativa('')
      setDataReprovacaoAdministrativa('')
      setDatasReprovacao(prev => {
        const novos = { ...prev }
        delete novos[faturamentoSelecionado.idFaturamento]
        return novos
      })
      setIdsSelecionados((prev) => {
        const n = new Set(prev)
        n.delete(faturamentoSelecionado.idFaturamento)
        return n
      })
      setFaturamentos(faturamentos.filter(f => f.idFaturamento !== faturamentoSelecionado.idFaturamento))
      setFaturamentoSelecionado(null)
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao reprovar faturamento')
    } finally {
      stopLoading()
    }
  }

  const cancelarReprovarAdministrativo = () => {
    if (faturamentoSelecionado) {
      setDatasReprovacao(prev => {
        const novos = { ...prev }
        delete novos[faturamentoSelecionado.idFaturamento]
        return novos
      })
    }
    setShowModalReprovarAdministrativo(false)
    setMotivoReprovacaoAdministrativa('')
    setDataReprovacaoAdministrativa('')
    setFaturamentoSelecionado(null)
  }

  const abrirConfirmacaoEdicao = (idFaturamento: number, campo: 'dataEnvioMomentum' | 'dataAprovacao' | 'dataFaturamento' | 'nf', valor: string) => {
    setPendenciaEdicao({ idFaturamento, campo, valor })
    setShowConfirmEdicao(true)
  }

  const confirmarEdicao = async () => {
    if (!pendenciaEdicao)
      return

    try {
      startLoading('Atualizando faturamento...')
      const payload: { nf?: string | null; dataEnvioMomentum?: string | null; dataAprovacao?: string | null; dataFaturamento?: string | null } = {}
      if (pendenciaEdicao.campo === 'dataEnvioMomentum')
        payload.dataEnvioMomentum = pendenciaEdicao.valor
      else if (pendenciaEdicao.campo === 'dataAprovacao')
        payload.dataAprovacao = pendenciaEdicao.valor
      else if (pendenciaEdicao.campo === 'dataFaturamento')
        payload.dataFaturamento = pendenciaEdicao.valor
      else
        payload.nf = pendenciaEdicao.valor

      await faturamentoService.atualizarCamposFaturamento(pendenciaEdicao.idFaturamento, payload)

      if (pendenciaEdicao.campo === 'dataEnvioMomentum')
        setDatasEnvioOrig(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: pendenciaEdicao.valor }))
      else if (pendenciaEdicao.campo === 'dataAprovacao')
        setDatasAprovacaoOrig(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: pendenciaEdicao.valor }))
      else if (pendenciaEdicao.campo === 'dataFaturamento')
        setDatasFaturamentoOrig(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: pendenciaEdicao.valor }))
      else
        setNfsOrig(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: pendenciaEdicao.valor }))

      setShowConfirmEdicao(false)
      setPendenciaEdicao(null)

      const valoresAtualizados = getValues()
      await onSubmitForm(valoresAtualizados)
    } catch (error: any) {
      toast.error('Erro', error.message || 'Erro ao atualizar faturamento')
      cancelarEdicao()
    } finally {
      stopLoading()
    }
  }

  const cancelarEdicao = () => {
    if (!pendenciaEdicao)
      return

    if (pendenciaEdicao.campo === 'dataEnvioMomentum')
      setDatasEnvio(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: datasEnvioOrig[pendenciaEdicao.idFaturamento] || '' }))
    else if (pendenciaEdicao.campo === 'dataAprovacao')
      setDatasAprovacao(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: datasAprovacaoOrig[pendenciaEdicao.idFaturamento] || '' }))
    else if (pendenciaEdicao.campo === 'dataFaturamento')
      setDatasFaturamento(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: datasFaturamentoOrig[pendenciaEdicao.idFaturamento] || '' }))
    else
      setNfs(prev => ({ ...prev, [pendenciaEdicao.idFaturamento]: nfsOrig[pendenciaEdicao.idFaturamento] || '' }))

    setShowConfirmEdicao(false)
    setPendenciaEdicao(null)
  }

  const formatarData = (data?: string) => {
    if (!data) return '-'
    return formatDateToBrazilian(data) || '-'
  }

  const formatarMoeda = (valor: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor)
  }


  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Faturamento - Administrador</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gerencie e acompanhe todos os faturamentos
          </p>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-r from-brand-500 to-brand-600 rounded-lg flex items-center justify-center">
            <Filter className="w-5 h-5 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Filtros</h2>
            <p className="text-sm text-gray-600">Busque faturamentos com filtros avançados</p>
          </div>
        </div>

        <form 
          onSubmit={handleSubmit(onSubmitForm)} 
          className="space-y-4"
          noValidate
        >
          <input type="hidden" {...register('dataInicio')} />
          <input type="hidden" {...register('dataFim')} />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Ticket
              </label>
              <input
                type="text"
                {...register('numeroTicket')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Número do ticket"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome do Projeto
              </label>
              <input
                type="text"
                {...register('nomeProjeto')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Nome do projeto"
              />
            </div>
            <div ref={dropdownAnalistaRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Analista SMN
              </label>
              <button
                type="button"
                onClick={() => setDropdownAnalistaAberto(prev => !prev)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-left bg-white text-sm flex items-center justify-between"
              >
                <span className="truncate">
                  {analistasSelecionados.length === 0 || analistasSelecionados.length === analistasSMN.length
                    ? 'Todos'
                    : analistasSelecionados.length === 1
                      ? analistasSMN.find(a => a.pessoaId === analistasSelecionados[0])?.nome ?? '1 selecionado'
                      : `${analistasSelecionados.length} selecionados`}
                </span>
                <svg className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-150 ${dropdownAnalistaAberto ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {dropdownAnalistaAberto && (
                <div className="absolute z-20 top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={analistasSelecionados.length === analistasSMN.length && analistasSMN.length > 0}
                      ref={el => {
                        if (el) el.indeterminate = analistasSelecionados.length > 0 && analistasSelecionados.length < analistasSMN.length
                      }}
                      onChange={() => setAnalistasSelecionados(
                        analistasSelecionados.length === analistasSMN.length
                          ? []
                          : analistasSMN.map(a => a.pessoaId)
                      )}
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">Todos</span>
                  </label>
                  {analistasSMN.map(analista => (
                    <label key={analista.pessoaId} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={analistasSelecionados.includes(analista.pessoaId)}
                        onChange={() => setAnalistasSelecionados(prev =>
                          prev.includes(analista.pessoaId)
                            ? prev.filter(id => id !== analista.pessoaId)
                            : [...prev, analista.pessoaId]
                        )}
                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700">{analista.nome}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div ref={dropdownStatusRef} className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Situação
              </label>
              <button
                type="button"
                onClick={() => setDropdownStatusAberto(prev => !prev)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 text-left bg-white text-sm flex items-center justify-between"
              >
                <span className="truncate">
                  {statusSelecionados.length === 0 || statusSelecionados.length === statusFaturamento.length
                    ? 'Todos'
                    : statusSelecionados.length === 1
                      ? statusFaturamento.find(s => s.codigo === statusSelecionados[0])?.descricao ?? '1 selecionado'
                      : `${statusSelecionados.length} selecionados`}
                </span>
                <svg className={`w-4 h-4 flex-shrink-0 ml-2 transition-transform duration-150 ${dropdownStatusAberto ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
              </button>
              {dropdownStatusAberto && (
                <div className="absolute z-20 top-full left-0 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg">
                  <label className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100">
                    <input
                      type="checkbox"
                      checked={statusSelecionados.length === statusFaturamento.length && statusFaturamento.length > 0}
                      ref={el => {
                        if (el) el.indeterminate = statusSelecionados.length > 0 && statusSelecionados.length < statusFaturamento.length
                      }}
                      onChange={() => setStatusSelecionados(
                        statusSelecionados.length === statusFaturamento.length
                          ? []
                          : statusFaturamento.map(s => s.codigo)
                      )}
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                    <span className="text-sm text-gray-700">Todos</span>
                  </label>
                  {statusFaturamento.map(status => (
                    <label key={status.codigo} className="flex items-center gap-2 px-3 py-2 hover:bg-gray-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={statusSelecionados.includes(status.codigo)}
                        onChange={() => setStatusSelecionados(prev =>
                          prev.includes(status.codigo)
                            ? prev.filter(c => c !== status.codigo)
                            : [...prev, status.codigo]
                        )}
                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                      />
                      <span className="text-sm text-gray-700">{status.descricao}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                NF
              </label>
              <input
                type="text"
                {...register('numeroNotaFiscal')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200"
                placeholder="Número da nota fiscal"
              />
            </div>
          </div>

          <SeletorPeriodo
            tipoPeriodo={tipoPeriodo}
            onTipoPeriodoChange={setTipoPeriodo}
            dataReferencia={dataReferenciaPeriodo}
            onDataReferenciaChange={setDataReferenciaPeriodo}
            intervaloInicio={intervaloInicio}
            intervaloFim={intervaloFim}
            onIntervaloInicioChange={setIntervaloInicio}
            onIntervaloFimChange={setIntervaloFim}
            descricaoAjuda="(filtro pela data de cadastro do faturamento)"
            onAplicarConfirmado={() =>
              setPulsoBuscaAposAplicarPeriodo((p) => p + 1)
            }
          />
          {(errors.dataInicio?.message || errors.dataFim?.message) && (
            <p className="text-sm text-red-600">
              {errors.dataInicio?.message || errors.dataFim?.message}
            </p>
          )}

          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={limparFiltros}
              className="px-3 py-2 text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200 font-medium"
            >
              <X className="w-4 h-4 mr-2 inline" />
              Limpar Filtros
            </button>
            <button
              type="submit"
              disabled={isSearching}
              className="px-4 py-2 text-white bg-brand-500 rounded-lg hover:bg-brand-600 transition-all duration-200 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Search className="w-4 h-4 mr-2 inline" />
              Buscar
            </button>
          </div>
        </form>
      </div>

      {faturamentos.length > 0 && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
          {idsSelecionados.size > 0 && (
            <div className="mb-4 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-brand-200 bg-brand-50/90 px-4 py-3">
              <span className="text-sm text-gray-800">
                {idsSelecionados.size === 1
                  ? '1 item selecionado'
                  : `${idsSelecionados.size} itens selecionados`}
              </span>
              <span className="text-base font-semibold text-gray-900 tabular-nums">
                Total selecionado: {formatarMoeda(somaValorSelecionados)}
              </span>
            </div>
          )}
          <div className="mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Lista de Faturamentos</h3>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-50">
                  <th className="text-center text-sm font-medium text-gray-700 py-2 px-2 border-b w-10">
                    <input
                      type="checkbox"
                      title="Selecionar linhas desta página"
                      aria-label="Selecionar todas as linhas visíveis na página"
                      checked={idsPaginaAtual.length > 0 && todosDaPaginaSelecionados}
                      ref={(el) => {
                        if (!el)
                          return
                        const algumaNaPagina = idsPaginaAtual.some((id) => idsSelecionados.has(id))
                        el.indeterminate = algumaNaPagina && !todosDaPaginaSelecionados
                      }}
                      onChange={alternarSelecionarPaginaAtual}
                      className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                    />
                  </th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Projeto</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Responsável</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Situação</th>
                  <th className="text-right text-sm font-medium text-gray-700 py-2 px-3 border-b">Valor Total</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Dt. Envio Analista</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Dt. Envio Momentum</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Data Aprovação</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Data Faturamento</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">Data Reprovação</th>
                  <th className="text-left text-sm font-medium text-gray-700 py-2 px-3 border-b">NF</th>
                  <th className="text-center text-sm font-medium text-gray-700 py-2 px-3 border-b">Ações</th>
                </tr>
              </thead>
              <tbody>
                {faturamentosPagina.map((faturamento, index) => {
                  const idFat = faturamento.idFaturamento
                  const temEnvio = !!(datasEnvio[idFat])
                  const temAprovacao = !!(datasAprovacao[idFat])
                  const temReprovacao = !!(datasReprovacao[idFat]) || faturamento.statusCodigo === 5
                  const temNF = !!(nfs[idFat])
                  const temDataFaturamento = !!(datasFaturamento[idFat])

                  const envioMomentumDisabled = temAprovacao
                  const aprovacaoDisabled = !temEnvio || temReprovacao || temNF
                  const reprovacaoDisabled = !temEnvio || temAprovacao
                  const nfDisabled = !temEnvio || !temAprovacao || !temDataFaturamento || temReprovacao

                  return (
                  <tr key={idFat} className={`hover:bg-gray-50 ${index % 2 === 1 ? 'bg-gray-50' : ''}`}>
                    <td className="py-2 px-2 border-b text-center align-top">
                      <input
                        type="checkbox"
                        checked={idsSelecionados.has(idFat)}
                        onChange={() => alternarSelecaoPorId(idFat)}
                        className="rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                        aria-label={`Selecionar faturamento ${faturamento.projetoNome ?? idFat}`}
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-sm text-gray-900">
                      <div>
                        <p className="font-medium">{faturamento.projetoNome}</p>
                        {faturamento.projetoNumeroTicket && (
                          <p className="text-xs text-gray-500">Ticket: {faturamento.projetoNumeroTicket}</p>
                        )}
                      </div>
                    </td>
                    <td className="py-2 px-3 border-b text-sm text-gray-900">{faturamento.analistaSMNNome || '-'}</td>
                    <td className="py-2 px-3 border-b text-sm text-gray-900">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${obterClasseStatus(faturamento.statusCodigo)}`}>
                        {faturamento.statusDescricao || '-'}
                      </span>
                      {(() => {
                        const etapa = etapasFaturamento.find(e => e.codigoEtapaFaturamento === faturamento.codigoEtapaFaturamento)
                        return etapa ? (
                          <p className="text-xs text-gray-500 mt-0.5">{etapa.descricao} ({etapa.percentual}%)</p>
                        ) : null
                      })()}
                    </td>
                    <td className="py-2 px-3 border-b text-sm text-gray-900 text-right">{formatarMoeda(faturamento.valorTotal)}</td>
                    <td className="py-2 px-3 border-b text-sm text-gray-700">
                      {formatarData(faturamento.dataEnvioAnalista)}
                    </td>
                    <td className="py-2 px-3 border-b text-sm">
                      <InputDate
                        value={datasEnvio[idFat] || ''}
                        onChange={(valor) => setDatasEnvio(prev => ({ ...prev, [idFat]: valor }))}
                        onBlur={(valor) => {
                          const valorAtual = valor || ''
                          const valorOriginal = datasEnvioOrig[idFat] || ''
                          if (valorAtual !== valorOriginal)
                            abrirConfirmacaoEdicao(idFat, 'dataEnvioMomentum', valorAtual)
                        }}
                        disabled={envioMomentumDisabled}
                        className="w-36 px-2 py-1 border border-gray-300 rounded text-xs focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all duration-200 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        showError={false}
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-sm">
                      <InputDate
                        value={datasAprovacao[idFat] || ''}
                        onChange={(valor) => setDatasAprovacao(prev => ({ ...prev, [idFat]: valor }))}
                        onBlur={(valor) => {
                          const valorAtual = valor || ''
                          const valorOriginal = datasAprovacaoOrig[idFat] || ''
                          if (valorAtual !== valorOriginal)
                            abrirConfirmacaoEdicao(idFat, 'dataAprovacao', valorAtual)
                        }}
                        disabled={aprovacaoDisabled}
                        className="w-36 px-2 py-1 border border-gray-300 rounded text-xs focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all duration-200 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        showError={false}
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-sm">
                      <InputDate
                        value={datasFaturamento[idFat] || ''}
                        onChange={(valor) => setDatasFaturamento(prev => ({ ...prev, [idFat]: valor }))}
                        onBlur={(valor) => {
                          const valorAtual = valor || ''
                          const valorOriginal = datasFaturamentoOrig[idFat] || ''
                          if (valorAtual !== valorOriginal)
                            abrirConfirmacaoEdicao(idFat, 'dataFaturamento', valorAtual)
                        }}
                        disabled={!temAprovacao || temReprovacao}
                        className="w-36 px-2 py-1 border border-gray-300 rounded text-xs focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all duration-200 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        showError={false}
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-sm">
                      <InputDate
                        value={datasReprovacao[idFat] || ''}
                        onChange={(valor) => setDatasReprovacao(prev => ({ ...prev, [idFat]: valor }))}
                        onBlur={() => handleDataReprovacaoBlur(faturamento)}
                        disabled={reprovacaoDisabled}
                        className="w-36 px-2 py-1 border border-gray-300 rounded text-xs focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all duration-200 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        showError={false}
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-sm">
                      <input
                        type="text"
                        value={nfs[idFat] || ''}
                        onChange={(e) => {
                          const valor = e.target.value.replace(/[^0-9]/g, '').slice(0, 10)
                          setNfs(prev => ({ ...prev, [idFat]: valor }))
                        }}
                        onBlur={() => {
                          const valorAtual = nfs[idFat] || ''
                          const valorOriginal = nfsOrig[idFat] || ''
                          if (valorAtual !== valorOriginal)
                            abrirConfirmacaoEdicao(idFat, 'nf', valorAtual)
                        }}
                        disabled={nfDisabled}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-xs focus:border-brand-500 focus:ring-1 focus:ring-brand-500 transition-all duration-200 hover:border-gray-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                      />
                    </td>
                    <td className="py-2 px-3 border-b text-center">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          onClick={() => handleVisualizar(faturamento)}
                          className="p-2 rounded-lg hover:text-blue-600 hover:bg-blue-50 transition-all"
                          title="Visualizar"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDownloadDocumentos(faturamento.idFaturamento)}
                          className="p-2 rounded-lg hover:text-gray-600 hover:bg-gray-50 transition-all"
                          title="Download documentos"
                        >
                          <Download className="w-4 h-4" />
                        </button>
                        {!faturamento.dataAprovacao && (
                          <button
                            onClick={() => handleReprovar(faturamento)}
                            className="p-2 rounded-lg hover:text-red-600 hover:bg-red-50 transition-all"
                            title="Reprovar"
                          >
                            <XCircle className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
          {faturamentos.length > PAGE_SIZE && (
            <div className="mt-4 flex items-center justify-between border-t border-gray-200 pt-4">
              <p className="text-sm text-gray-600">
                Exibindo {(pagina - 1) * PAGE_SIZE + 1} a {Math.min(pagina * PAGE_SIZE, faturamentos.length)} de {faturamentos.length} registros
              </p>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  disabled={pagina <= 1}
                  className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-sm font-medium text-gray-700 min-w-[120px] text-center">
                  Página {pagina} de {totalPaginas}
                </span>
                <button
                  type="button"
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  disabled={pagina >= totalPaginas}
                  className="p-2 rounded-lg border border-gray-300 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  title="Próxima página"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {buscaRealizada && faturamentos.length === 0 && !isSearching && (
        <div className="bg-white rounded-xl p-6 border border-gray-200 shadow-theme-sm">
          <div className="text-center py-12">
            <DollarSign className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Nenhum faturamento encontrado
            </h3>
            <p className="text-gray-600">
              Utilize os filtros acima para buscar faturamentos
            </p>
          </div>
        </div>
      )}

      {/* Modal de Detalhes */}
      <Modal
        isOpen={showModalDetalhes}
        onClose={() => {
          setShowModalDetalhes(false)
          setDetalhesFaturamento(null)
          setFaturamentoSelecionado(null)
        }}
        title="Detalhes do Faturamento"
        subtitle={faturamentoSelecionado?.projetoNome}
        size="4xl"
        icon={
          <div className="w-10 h-10 bg-brand-100 text-brand-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <Eye className="w-5 h-5" />
          </div>
        }
      >
        {detalhesFaturamento && faturamentoSelecionado && (
          <FaturamentoDetalhesConteudo
            detalhesFaturamento={detalhesFaturamento}
            faturamentoSelecionado={faturamentoSelecionado}
            etapasFaturamento={etapasFaturamento}
            faturamentosProjeto={faturamentosProjeto}
          />
        )}
      </Modal>

      {/* Modal de Reprovação */}
      <Modal
        isOpen={showModalReprovar}
        onClose={() => {
          setShowModalReprovar(false)
          setFaturamentoSelecionado(null)
          setMotivoReprovacao('')
        }}
        title="Reprovar Faturamento"
        subtitle={faturamentoSelecionado?.projetoNome}
        size="lg"
        icon={
          <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <XCircle className="w-5 h-5" />
          </div>
        }
      >
        <div className="space-y-4 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da Reprovação <span className="text-red-500">*</span>
            </label>
            <textarea
              value={motivoReprovacao}
              onChange={(e) => setMotivoReprovacao(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Informe o motivo da reprovação"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={() => {
                setShowModalReprovar(false)
                setFaturamentoSelecionado(null)
                setMotivoReprovacao('')
              }}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarReprovar}
              disabled={!motivoReprovacao.trim()}
              className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reprovar
            </button>
          </div>
        </div>
      </Modal>

      {/* Modal de Reprovação Administrativa */}
      <Modal
        isOpen={showModalReprovarAdministrativo}
        onClose={cancelarReprovarAdministrativo}
        title="Reprovar Faturamento (Administrativo)"
        subtitle={faturamentoSelecionado?.projetoNome}
        size="lg"
        icon={
          <div className="w-10 h-10 bg-red-100 text-red-600 rounded-lg flex items-center justify-center flex-shrink-0 min-w-[2.5rem] min-h-[2.5rem]">
            <XCircle className="w-5 h-5" />
          </div>
        }
      >
        <div className="space-y-4 p-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Data da Reprovação <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              value={dataReprovacaoAdministrativa}
              readOnly
              className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed transition-all duration-200"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Motivo da Reprovação <span className="text-red-500">*</span>
            </label>
            <textarea
              value={motivoReprovacaoAdministrativa}
              onChange={(e) => setMotivoReprovacaoAdministrativa(e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-500 focus:border-transparent transition-all duration-200 resize-none"
              placeholder="Informe o motivo da reprovação"
            />
          </div>
          <div className="flex justify-end gap-3 pt-4">
            <button
              onClick={cancelarReprovarAdministrativo}
              className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-all duration-200"
            >
              Cancelar
            </button>
            <button
              onClick={confirmarReprovarAdministrativo}
              disabled={!motivoReprovacaoAdministrativa.trim() || !dataReprovacaoAdministrativa.trim()}
              className="px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Reprovar
            </button>
          </div>
        </div>
      </Modal>

      {pendenciaEdicao && (
        <ConfirmDialog
          isOpen={showConfirmEdicao}
          onClose={cancelarEdicao}
          onConfirm={confirmarEdicao}
          title="Confirmar Alteração"
          message="Deseja confirmar esta alteração?"
          confirmText="Confirmar"
          cancelText="Cancelar"
          variant="warning"
        />
      )}
    </div>
  )
}

